import React, { useRef } from 'react';
import { useState } from 'react';
import { useUpdate, useNotify, Confirm } from 'react-admin';
import { Stack, FormControl, Button } from '@mui/material';
import { UserStatusObj } from '../../../utils/enum';
import MAutoComplete from '../../../components/controls/MAutoComplete';
import { refreshCeilsAGrid } from '../../../utils/common';
import { IAssignmentRecord, IAssignmentUser as IAssignmentUserOption } from '../types';

/** Props for {@link AssignmentUser}. */
export interface IAssignmentUserProps {
    /** The AG Grid row's underlying `OrderAssignments` record being edited. */
    data: IAssignmentRecord;
    /** AG Grid cell-editor API, used to stop editing after save/cancel. */
    api: any;
    /** Current cell value (the assigned user's id) used to pre-select `user`. */
    value: number | string;
    /** List of assignable processors. */
    listUser: IAssignmentUserOption[];
}

/**
 * AG Grid cell editor for the `assignedTo` column: an autocomplete of
 * `listUser` plus a confirm dialog that persists the assignment via
 * `useUpdate`.
 * @param props - AG Grid cell editor props (`data`, `api`, `value`, ...) plus `listUser`.
 */
const AssignmentUser: React.FC<IAssignmentUserProps> = (props) => {
    const { listUser } = props;
    const refInput = useRef<any>(null);
    const [user, setUser] = useState<IAssignmentUserOption>();
    const [open, setOpen] = useState<boolean>(false);//react-admin confirm
    const saveEdit = () => setOpen(true);
    const [handleUpdate] = useUpdate();
    const notify = useNotify();
    const handleDialogClose = () => setOpen(false);
    const onUserChange = (value: any) => {
        setUser(value);
    };

    React.useEffect(() => {
        setTimeout(() => refInput.current.click(), 100); // To show options of autocomplete
    }, [listUser]);

    React.useEffect(() => {
        initUser(props.value);
    }, [props.value]);

    const handleConfirm = () => {
        let order = props.data;
        order.assignedTo = user!.name;
        order.status = UserStatusObj.Actived;
        order.aliasName = user!.value;
        handleUpdate('OrderAssignments', { id: order.id, data: order }, {
            onSuccess: (response) => {
                notify('Update successfully', { type: 'success', autoHideDuration: 2000 });
                props.api.stopEditing();
                props.data['assignedDate'] = response?.assignedDate;
                props.data['assignedTo'] = response?.assignedTo;
                refreshCeilsAGrid(props, ['assignedDate', 'assignedTo']);
            },
            onError: (error: any) => {
                notify(`Update error: ${error.message}`, { type: 'error', autoHideDuration: 5000 });
                (props.data as any).tags = props.value;
                props.api.stopEditing();
            },
        });
        setOpen(false);
    };

    function initUser (value: number | string) {
        // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
        let _user = listUser.find((x) => x.id == value);
        setUser(_user);
    }

    return (
        <FormControl sx={{ width: 250 }}>
            <MAutoComplete
                label={'Select user (*)'}
                refInput={refInput}
                isMulti={false}
                choices={listUser as any}
                value={user as any}
                defaultValue={props.value as any}
                onChange={onUserChange}
                error={!user}
            />
            <Stack sx={{ mt: 2 }} direction="row" spacing={2}>
                <Button onClick={saveEdit} variant="contained" disabled={!user}>Save</Button>
                <Button onClick={() => { props.api.stopEditing(); }} variant="outlined">Cancel</Button>
            </Stack>
            <Confirm isOpen={open} title='Assign Order' content='Are you sure want to assign to this user?' onConfirm={handleConfirm} onClose={handleDialogClose} />
        </FormControl>
    );
};

export default AssignmentUser;
